import React from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  Stack,
  Typography,
} from '@mui/material';
import withWebsiteContent from '../../../common/withWebsiteContent';
import { sectionScrollMarginSx } from '../../../common/hooks/useScrollSpyHash';
import Gallery, { GALLERY_PREVIEW_MODE } from './Gallery';

const isExternalUrl = (url) =>
  typeof url === 'string' &&
  (/^https?:\/\//i.test(url) || url.startsWith('//'));

/**
 * Resolves a filename stem to a full URL from the component `images` list.
 */
const resolveImageUrl = (imageKey, images) => {
  if (!imageKey || !Array.isArray(images) || images.length === 0) return null;
  for (const url of images) {
    const stem = url.split('/').pop()?.replace(/\.[^.]+$/, '') ?? '';
    if (stem === imageKey) return url;
  }
  return null;
};

const normalizeImageKeys = (feature) => {
  if (Array.isArray(feature.images) && feature.images.length > 0) {
    return feature.images.filter((k) => typeof k === 'string' && k.trim() !== '');
  }
  if (typeof feature.image === 'string' && feature.image.trim() !== '') {
    return [feature.image.trim()];
  }
  return [];
};

/**
 * Extended features — stacked rows; each row is gallery ({@link GALLERY_PREVIEW_MODE.SINGLE_THUMB}) left, text right (column on xs).
 *
 * defaultProps shape (metadata.json defaultProps section):
 * {
 *   "title": "...",
 *   "subtitle": "...",
 *   "features": [
 *     { "id": "...", "name": "...", "description": "...",
 *       "images": ["stem-one", "stem-two"] }
 *   ],
 *   "buttonLabel": "...",
 *   "buttonUrl": "/path"
 * }
 */
export const FeaturesExtendedPure = ({
  id,
  title = '',
  subtitle = '',
  features = [],
  images = [],
  buttonLabel,
  buttonUrl,
}) => {
  const resolvedFeatures = features.map((f) => {
    const keys = normalizeImageKeys(f);
    const imageUrls = keys
      .map((key) => resolveImageUrl(key, images))
      .filter(Boolean);
    return { ...f, imageUrls };
  });

  const hasActionButton =
    typeof buttonLabel === 'string' &&
    buttonLabel.trim() !== '' &&
    typeof buttonUrl === 'string' &&
    buttonUrl.trim() !== '';

  if (resolvedFeatures.length === 0) return null;

  return (
    <Box id={id} component="section" sx={sectionScrollMarginSx}>
      <Container sx={{ py: 8 }}>
      {title && (
        <Typography
          variant="h4"
          component="h2"
          sx={{ textAlign: 'center', mb: subtitle ? 2 : 4, fontWeight: 600 }}
        >
          {title}
        </Typography>
      )}
      {subtitle && (
        <Typography
          variant="h6"
          component="p"
          color="text.secondary"
          sx={{ textAlign: 'center', mb: 6 }}
        >
          {subtitle}
        </Typography>
      )}
      <Stack spacing={6} alignItems="stretch">
        {resolvedFeatures.map((feature, index) => (
          <Box
            key={feature.id || index}
            sx={{
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              overflow: 'hidden',
              bgcolor: 'background.paper',
            }}
          >
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={{ xs: 2, md: 3 }}
              alignItems={{ xs: 'stretch', md: 'flex-start' }}
              sx={{ p: { xs: 2, sm: 3 } }}
            >
              {feature.imageUrls.length > 0 && (
                <Box
                  sx={{
                    flexShrink: 0,
                    width: { xs: '100%', md: 'min(42%, 420px)' },
                    maxWidth: { md: 480 },
                  }}
                >
                  <Gallery
                    pictureUrls={feature.imageUrls}
                    previewMode={GALLERY_PREVIEW_MODE.SINGLE_THUMB}
                    previewAspectRatio="16/9"
                  />
                </Box>
              )}
              {(feature.name || feature.description) && (
                <Box sx={{ flex: { md: '1 1 0%' }, minWidth: 0, pt: { md: 0.5 } }}>
                  {feature.name && (
                    <Typography variant="h5" component="h3" gutterBottom fontWeight={600}>
                      {feature.name}
                    </Typography>
                  )}
                  {feature.description && (
                    <Typography
                      variant="body1"
                      color="text.secondary"
                      sx={{ whiteSpace: 'pre-line' }}
                    >
                      {feature.description}
                    </Typography>
                  )}
                </Box>
              )}
            </Stack>
          </Box>
        ))}
      </Stack>
      {hasActionButton && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
          {isExternalUrl(buttonUrl) ? (
            <Button
              component="a"
              href={buttonUrl}
              variant="contained"
              size="large"
              rel="noopener noreferrer"
              target="_blank"
            >
              {buttonLabel}
            </Button>
          ) : (
            <Button component={Link} to={buttonUrl} variant="contained" size="large">
              {buttonLabel}
            </Button>
          )}
        </Box>
      )}
      </Container>
    </Box>
  );
};

const FeaturesExtended = withWebsiteContent(FeaturesExtendedPure);
FeaturesExtended.displayName = 'FeaturesExtended';

export default FeaturesExtended;
